import { supabase } from './supabase';
import type { Treasure, AvailableCity, CountryConfig } from '../types';

const SUPABASE_URL = 'https://vhvkaisivmqxvljteyou.supabase.co';

export class TreasureService {
  async createPaymentIntent(partnerId: string, priceCents: number) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/payment-create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
      },
      body: JSON.stringify({
        partner_id: partnerId,
        price_cents: priceCents
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create payment intent');
    }

    return response.json();
  }

  async getPartnerTreasures(partnerId: string, forceRefresh = false): Promise<Treasure[]> {
    let query = supabase
      .from('treasures')
      .select('*')
      .eq('purchased_by', partnerId)
      .order('created_at', { ascending: false });
    
    // If forcing refresh, add a random parameter to prevent caching
    if (forceRefresh) {
      console.log('Force refreshing treasure data...');
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  }

  async updateCustomRewardText(treasureId: string, customText: string): Promise<void> {
    console.log('Attempting to update treasure:', treasureId, 'with text:', customText);
    
    const { data, error } = await supabase
      .from('treasures')
      .update({ 
        custom_merchant_reward_text: customText || null
      })
      .eq('id', treasureId)
      .select(); // Add select to ensure the operation completes properly

    if (error) {
      console.error('Database update error:', error);
      throw new Error(error.message);
    }
    
    console.log('Update successful, returned data:', data);
  }

  async getAvailableCities(): Promise<AvailableCity[]> {
    // Get current partner's country code
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get partner information to determine country
    const { data: partner, error: partnerError } = await supabase
      .from('partners')
      .select('country_code')
      .eq('id', user.id)
      .single();

    if (partnerError) {
      console.error('Error fetching partner country:', partnerError);
      // Fallback to all cities if partner country not found
    }

    const partnerCountry = partner?.country_code || 'SE'; // Default to Sweden

    // Get municipalities (admin_level 7) filtered by partner's country
    const { data, error } = await supabase
      .from('available_cities')
      .select('*')
      .eq('country_code', partnerCountry)
      .eq('admin_level', 7) // Only municipalities for first-level selection
      .order('name', { ascending: true });

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  }

  async getCitiesInMunicipality(municipalityId: number): Promise<AvailableCity[]> {
    // Get sub-places for this municipality from the dedicated table
    const { data: subPlaces, error } = await supabase
      .from('available_sub_places')
      .select('*')
      .eq('municipality_id', municipalityId)
      .order('population', { ascending: false }) // Largest places first
      .order('name', { ascending: true }); // Then alphabetical

    if (error) {
      throw new Error(error.message);
    }

    // Convert sub-places to AvailableCity format for compatibility
    const cities: AvailableCity[] = (subPlaces || []).map(place => ({
      id: place.id,
      name: place.name,
      country_code: place.country_code,
      lat: place.lat,
      lng: place.lng,
      population: place.population,
      admin_level: place.admin_level,
      place_type: place.place_type,
      parent_id: place.municipality_id, // For compatibility
      created_at: place.created_at
    }));

    return cities;
  }

  async getCityNamesByIds(cityIds: number[]): Promise<{ [key: number]: string }> {
    if (cityIds.length === 0) return {};

    // Get city names from available_sub_places table
    const { data: subPlaces, error } = await supabase
      .from('available_sub_places')
      .select('id, name')
      .in('id', cityIds);

    if (error) {
      console.error('Error fetching city names:', error);
      return {};
    }

    // Convert to lookup object
    const cityLookup: { [key: number]: string } = {};
    (subPlaces || []).forEach(place => {
      cityLookup[place.id] = place.name;
    });

    return cityLookup;
  }

  async getCountryConfig(): Promise<CountryConfig | null> {
    // Get current partner's country code
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get partner information to determine country
    const { data: partner, error: partnerError } = await supabase
      .from('partners')
      .select('country_code')
      .eq('id', user.id)
      .single();

    if (partnerError) {
      console.error('Error fetching partner country:', partnerError);
      return null;
    }

    const partnerCountry = partner?.country_code || 'SE';

    // Get country configuration
    const { data, error } = await supabase
      .from('country_configs')
      .select('*')
      .eq('country_code', partnerCountry)
      .single();

    if (error) {
      console.error('Error fetching country config:', error);
      return null;
    }

    return data;
  }

  async getPlaceTypeLabel(): Promise<string> {
    const countryConfig = await this.getCountryConfig();
    
    if (!countryConfig) {
      return 'location'; // Fallback
    }

    // Get the highest priority place type (lowest admin level)
    const sortedLevels = countryConfig.admin_levels.sort((a, b) => a - b);
    const highestLevel = sortedLevels[0];
    
    // Map admin levels to place types based on OSM standards
    const levelToTypeMap: { [key: number]: string } = {
      7: 'municipality',
      8: 'city',
      9: 'district',
      10: 'neighborhood'
    };

    const placeType = levelToTypeMap[highestLevel] || countryConfig.place_types[0] || 'location';
    
    // Capitalize first letter for UI display
    return placeType.charAt(0).toUpperCase() + placeType.slice(1);
  }

  async scheduleTokenPublishing(
    treasureId: string, 
    publishAt: string, 
    publishCityIds: number[] // Changed to array for multiple cities
  ): Promise<void> {
    const { error } = await supabase
      .from('treasures')
      .update({ 
        status: 'scheduled', // Set status to scheduled when scheduling publishing
        publish_at: publishAt,
        publish_city_ids: publishCityIds // Use new array field
      })
      .eq('id', treasureId)
      .select(); // Add select to ensure the operation completes properly

    if (error) {
      console.error('Database update error:', error);
      throw new Error(error.message);
    }
  }

  async cancelTokenPublishing(treasureId: string): Promise<void> {
    const { error } = await supabase
      .from('treasures')
      .update({ 
        status: 'draft', // Revert status back to draft when canceling
        publish_at: null,
        publish_city_id: null,
        publish_city_ids: null // Also clear the new array field
      })
      .eq('id', treasureId)
      .select(); // Add select to ensure the operation completes properly

    if (error) {
      console.error('Database update error:', error);
      throw new Error(error.message);
    }
  }
}
